import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode, useEffect } from "react";
import ReactDOM from "react-dom/client";

// Import the generated route tree
import { routeTree } from "./routeTree.gen.ts";

import { type QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TRPCProvider, queryClient, trpc, trpcClient } from "~/lib/trpc";
import reportWebVitals from "./reportWebVitals.ts";

import { useAuth, useUser } from "@clerk/clerk-react";
import { AuthProvider } from "~/components/auth-provider.tsx";
import { ThemeProvider } from "~/components/theme-provider.tsx";

import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";

import "@fontsource/geist-sans/400.css";
import "@fontsource/geist-sans/500.css";
import "@fontsource/geist-sans/600.css";
import "@fontsource/geist-sans/700.css";
import "@fontsource/geist-sans/800.css";
import { StudioManager } from "~/components/studio-manager/studio-manager.tsx";
import { Tracking } from "~/instrumentation/tracking.ts";
import "./styles.css";

export interface RouterContext {
  queryClient: QueryClient;
  trpc: typeof trpc;
  auth: ReturnType<typeof useAuth>;
  user: {
    id?: string;
    email?: string;
  };
}

const context: RouterContext = {
  queryClient,
  trpc,
  auth: null!, // This will be set in the RouterProvider
  user: null!, // This will be set in the RouterProvider
};

// Create a new router instance
export const router = createRouter({
  routeTree,
  context,
  defaultPreload: "intent",
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
  defaultViewTransition: false,
});

export type AppRoute = keyof typeof router.routesByPath;

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const Router = () => {
  const auth = useAuth();
  const { user, isLoaded: userLoaded } = useUser();

  useEffect(() => {
    Tracking.init();
  }, []);

  useEffect(() => {
    if (auth.userId) {
      Tracking.identify({
        userId: auth.userId,
        email: user?.emailAddresses[0]?.emailAddress,
        orgId: auth.orgId ?? undefined,
      });
    }

    if (auth.isLoaded) {
      router.invalidate();
    }
  }, [auth.isLoaded, user, auth.orgId]);

  return auth.isLoaded && userLoaded ? (
    <RouterProvider
      router={router}
      context={{
        auth,
        user: { id: user?.id, email: user?.emailAddresses[0]?.emailAddress },
      }}
    />
  ) : null;
};

// Render the app
const rootElement = document.getElementById("app");
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <PostHogProvider client={posthog}>
        <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
          <AuthProvider>
            <QueryClientProvider client={queryClient}>
              <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
                <StudioManager>
                  <Router />
                </StudioManager>
              </TRPCProvider>
            </QueryClientProvider>
          </AuthProvider>
        </ThemeProvider>
      </PostHogProvider>
    </StrictMode>,
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
